// app/javascript/controllers/header_search_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  
  // デバウンス関数を最初に定義
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
  
  connect() {
  }

  openSearch(event) {
    event.preventDefault();
    
    // より確実に既存の検索オーバーレイを探す
    let overlay = document.getElementById('search-overlay');
    let panel = document.getElementById('search-panel');
    
    // DOMの読み込みを少し待ってから再チェック
    if (!overlay) {
      setTimeout(() => {
        overlay = document.getElementById('search-overlay');
        panel = document.getElementById('search-panel');
        
        if (overlay && panel) {
          this.openExistingOverlay(overlay, panel);
        } else {
          this.createAndOpenOverlay();
        }
      }, 100);
      return;
    }
    
    if (overlay && panel) {
      this.openExistingOverlay(overlay, panel);
    } else {
      this.createAndOpenOverlay();
    }
  }

  openExistingOverlay(overlay, panel) {
    
    // Method 1: グローバル関数を使用
    if (window.openSearchOverlay && typeof window.openSearchOverlay === 'function') {
      window.openSearchOverlay();
      return;
    }
    
    // Method 2: Stimulusコントローラーを直接取得
    try {
      const searchOverlayController = this.application?.getControllerForElementAndIdentifier(overlay, 'search-overlay');
      if (searchOverlayController && searchOverlayController.open) {
        searchOverlayController.open();
        return;
      }
    } catch (error) {
    }
    
    // Method 3: カスタムイベントを発火
    try {
      const openEvent = new CustomEvent('openSearchOverlay', { bubbles: true });
      document.dispatchEvent(openEvent);
      return;
    } catch (error) {
    }
    
    // Fallback: 直接DOM操作
    overlay.classList.remove('hidden');
    
    // パネルをスライドイン
    setTimeout(() => {
      panel.style.transform = 'translateX(0)';
    }, 10);
    
    // bodyのスクロールを防ぐ
    document.body.style.overflow = 'hidden';
    
  }

  createAndOpenOverlay() {
    // 特集ページかどうかを判定
    const isFeaturePage = window.location.pathname.includes('/feature/') || 
                         document.body.classList.contains('feature-page') ||
                         document.querySelector('meta[name="page-type"]')?.getAttribute('content') === 'feature';
    
    // 動的にオーバーレイを作成（実際のデザインに合わせて）
    const overlay = document.createElement('div');
    overlay.id = 'search-overlay';
    overlay.className = 'fixed inset-0 z-50';
    
    // オーバーレイを閉じる関数
    const closeOverlay = () => {
      const panel = overlay.querySelector('#search-panel');
      panel.style.transform = 'translateX(100%)';
      setTimeout(() => {
        overlay.remove();
        document.body.style.overflow = '';
      }, 300);
    };
    
    // 件数を更新する関数
    const updateCount = async (formData) => {
      const recruitCountElement = overlay.querySelector('#recruit-count');
      const companyCountElement = overlay.querySelector('#company-count');
      
      try {
        const response = await fetch('/api/companies_search/count', {
          method: 'POST',
          body: formData,
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          recruitCountElement.textContent = data.recruit_count || 0;
          companyCountElement.textContent = data.company_count || 0;
        } else {
          console.error('Failed to fetch count:', response.statusText);
        }
      } catch (error) {
        console.error('Error fetching count:', error);
      }
    };
    
    // フォーム変更時に件数を更新
    const handleFormChange = () => {
      const form = overlay.querySelector('form');
      const formData = new FormData(form);
      updateCount(formData);
    };
    
    overlay.innerHTML = `
      <div class="absolute inset-0 bg-black/70"></div>
      <div id="search-panel" class="absolute top-0 right-0 h-full bg-gray-100 shadow-xl transition-transform duration-300 ease-in-out w-full md:w-1/3 lg:w-1/4 flex flex-col" style="transform: translateX(100%)">
        <!-- ヘッダー -->
        <div class="flex justify-between items-center p-6 bg-white text-gray-800 flex-shrink-0 border-b border-gray-200">
          <h2 class="text-xl font-bold">求人をさがす</h2>
          <button class="text-gray-600 hover:text-gray-800 transition-colors close-button">
            <i class="fas fa-times text-2xl"></i>
          </button>
        </div>
        
        <!-- 検索フォーム -->
        <div class="flex-1 overflow-y-auto flex flex-col">
          <form action="/companies_search/results" method="post" class="flex flex-col h-full" ${isFeaturePage ? 'data-turbo="false"' : ''}>
            <!-- フォーム内容 -->
            <div class="flex-1 overflow-y-auto p-6 pb-4">
              <!-- 件数表示 -->
              <div class="mb-4 text-center">
                <div class="text-sm text-gray-600 transition-all duration-300 rounded p-2">
                  <div class="font-medium text-blue-600">
                    <span id="recruit-count">0</span>件の求人が見つかりました
                  </div>
                  <div class="text-xs text-gray-500 mt-1">
                    <span id="company-count">0</span>件の企業が見つかりました
                  </div>
                </div>
              </div>
              
              <!-- キーワード検索 -->
              <div class="mb-6">
                <label class="block text-sm font-bold text-gray-800 mb-2">キーワード</label>
                <input type="text" name="keyword" placeholder="職種、会社名、仕事内容を入力" 
                       class="w-full px-4 py-3 bg-white rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors shadow-sm form-field">
              </div>
              
              <!-- 採用対象 -->
              <div class="mb-6">
                <label class="block text-sm font-bold text-gray-800 mb-2">採用対象</label>
                <select name="target" class="w-full px-4 py-3 bg-white rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors shadow-sm form-field">
                  <option value="">すべて</option>
                  <option value="新卒">新卒</option>
                  <option value="新卒(高校)">新卒(高校)</option>
                  <option value="一般">一般</option>
                </select>
              </div>
              
              <!-- 勤務地 -->
              <div class="mb-6">
                <label class="block text-sm font-bold text-gray-800 mb-2">勤務地</label>
                <select name="area" class="w-full px-4 py-3 bg-white rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors shadow-sm form-field">
                  <option value="">すべて</option>
                  <option value="出雲">出雲</option>
                  <option value="平田">平田</option>
                  <option value="佐田">佐田</option>
                  <option value="多伎">多伎</option>
                  <option value="湖陵">湖陵</option>
                  <option value="大社">大社</option>
                  <option value="斐川">斐川</option>
                </select>
              </div>
              
              <!-- 業種 -->
              <div class="mb-6">
                <label class="block text-sm font-bold text-gray-800 mb-2">業種</label>
                <select name="job_type" class="w-full px-4 py-3 bg-white rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors shadow-sm form-field">
                  <option value="">すべて</option>
                  <option value="メーカー">メーカー</option>
                  <option value="商社">商社</option>
                  <option value="流通・小売">流通・小売</option>
                  <option value="建設・不動産">建設・不動産</option>
                  <option value="サービス">サービス</option>
                  <option value="医療・福祉">医療・福祉</option>
                  <option value="教育">教育</option>
                  <option value="金融">金融</option>
                  <option value="IT・情報通信">IT・情報通信</option>
                  <option value="運輸・物流">運輸・物流</option>
                  <option value="官公庁・団体">官公庁・団体</option>
                  <option value="その他">その他</option>
                </select>
              </div>
              
              <!-- こだわり条件 -->
              <div class="mb-6">
                <label class="block text-sm font-bold text-gray-800 mb-2">こだわり条件（複数選択可）</label>
                <div class="relative custom-dropdown">
                  <button type="button" class="w-full px-4 py-3 bg-white rounded-xl text-left focus:outline-none hover:shadow-md transition-colors shadow-sm dropdown-button">
                    <span class="button-text">こだわり条件を選択 ▼</span>
                  </button>
                  
                  <div class="absolute z-50 w-full bg-white rounded-xl shadow-lg max-h-48 overflow-y-auto hidden dropdown-menu">
                    <div class="sticky top-0 bg-gray-50 px-3 py-2 rounded-t-xl text-xs text-gray-600 font-medium">
                      条件を選択してください（複数選択可能）
                    </div>
                    <div class="py-1">
                      <div class="px-3 py-2 cursor-pointer hover:bg-gray-100 transition-colors" data-value="週休２日制">
                        <input type="checkbox" name="point[]" value="週休２日制" class="mr-2 checkbox-field">週休２日制
                      </div>
                      <div class="px-3 py-2 cursor-pointer hover:bg-gray-100 transition-colors" data-value="土日休み">
                        <input type="checkbox" name="point[]" value="土日休み" class="mr-2 checkbox-field">土日休み
                      </div>
                      <div class="px-3 py-2 cursor-pointer hover:bg-gray-100 transition-colors" data-value="県外転勤なし">
                        <input type="checkbox" name="point[]" value="県外転勤なし" class="mr-2 checkbox-field">県外転勤なし
                      </div>
                      <div class="px-3 py-2 cursor-pointer hover:bg-gray-100 transition-colors" data-value="残業なし">
                        <input type="checkbox" name="point[]" value="残業なし" class="mr-2 checkbox-field">残業なし
                      </div>
                      <div class="px-3 py-2 cursor-pointer hover:bg-gray-100 transition-colors" data-value="車通勤可">
                        <input type="checkbox" name="point[]" value="車通勤可" class="mr-2 checkbox-field">車通勤可
                      </div>
                      <div class="px-3 py-2 cursor-pointer hover:bg-gray-100 transition-colors" data-value="制服貸与">
                        <input type="checkbox" name="point[]" value="制服貸与" class="mr-2 checkbox-field">制服貸与
                      </div>
                      <div class="px-3 py-2 cursor-pointer hover:bg-gray-100 transition-colors" data-value="寮・社宅あり">
                        <input type="checkbox" name="point[]" value="寮・社宅あり" class="mr-2 checkbox-field">寮・社宅あり
                      </div>
                      <div class="px-3 py-2 cursor-pointer hover:bg-gray-100 transition-colors" data-value="交通費支給">
                        <input type="checkbox" name="point[]" value="交通費支給" class="mr-2 checkbox-field">交通費支給
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- 検索ボタン -->
            <div class="flex-shrink-0 px-6 py-4 bg-white border-t border-gray-200">
              <input type="hidden" name="authenticity_token" value="${document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''}">
              <button type="submit" class="w-full bg-orange-500 text-white py-3 px-4 rounded-xl hover:bg-orange-600 transition-colors font-bold text-lg shadow-md" ${isFeaturePage ? 'data-turbo="false"' : ''}>
                この条件で検索する
              </button>
            </div>
          </form>
        </div>
      </div>
    `;
    
    // イベントリスナーを追加
    const closeButton = overlay.querySelector('.close-button');
    const backgroundClick = overlay.querySelector('.absolute.inset-0');
    
    closeButton.addEventListener('click', closeOverlay);
    backgroundClick.addEventListener('click', closeOverlay);
    
    // こだわり条件のドロップダウン機能
    const dropdownButton = overlay.querySelector('.dropdown-button');
    const dropdownMenu = overlay.querySelector('.dropdown-menu');
    const buttonText = overlay.querySelector('.button-text');
    
    dropdownButton.addEventListener('click', (e) => {
      e.preventDefault();
      dropdownMenu.classList.toggle('hidden');
    });
    
    // チェックボックスの変更を監視（件数更新も含む）
    const checkboxes = overlay.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => {
        const checkedBoxes = overlay.querySelectorAll('input[type="checkbox"]:checked');
        if (checkedBoxes.length === 0) {
          buttonText.textContent = 'こだわり条件を選択 ▼';
        } else {
          buttonText.textContent = `${checkedBoxes.length}つの条件を選択中 ▼`;
        }
        // 件数更新
        handleFormChange();
      });
    });
    
    // フォームフィールドの変更を監視
    const formFields = overlay.querySelectorAll('.form-field');
    formFields.forEach(field => {
      field.addEventListener('change', handleFormChange);
      field.addEventListener('input', 
        this.debounce(handleFormChange, 300) // キーワード入力は300ms後に更新
      );
    });
    
    // ドロップダウン外をクリックしたら閉じる
    document.addEventListener('click', (e) => {
      if (!overlay.querySelector('.custom-dropdown').contains(e.target)) {
        dropdownMenu.classList.add('hidden');
      }
    });
    
    // フォーム送信時にオーバーレイを閉じる
    const form = overlay.querySelector('form');
    form.addEventListener('submit', () => {
      closeOverlay();
    });
    
    // ESCキーでも閉じる
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        closeOverlay();
        document.removeEventListener('keydown', handleEscape);
      }
    };
    document.addEventListener('keydown', handleEscape);
    
    // DOMに追加
    document.body.appendChild(overlay);
    
    // 表示アニメーション
    setTimeout(() => {
      const panel = overlay.querySelector('#search-panel');
      panel.style.transform = 'translateX(0)';
    }, 10);
    
    // bodyのスクロールを防ぐ
    document.body.style.overflow = 'hidden';
    
    // 初期件数を取得
    handleFormChange();
    
  }
}
